import React, {
  ComponentProps,
  createContext,
  FC,
  ReactNode,
  useMemo,
} from "react";
import { useDialog } from "@natera/platform/lib/hooks";
import {
  AcknowledgementDialog,
  CloseWidgetDialog,
  MessageFeedbackDialog,
} from "@app/pippa/components/dialogs";

type useDialogType = ReturnType<typeof useDialog>;

// Parameterised so `messageFeedbackDialog.open()` is checked against the dialog's own
// props — the bare `useDialogType` defaults to `Record<string, unknown>`, which would
// happily accept `open({})` and blow up at render. Derived from the component rather
// than restating `{ messageId: string }`, so it follows the props automatically.
type MessageFeedbackDialogController = ReturnType<
  typeof useDialog<ComponentProps<typeof MessageFeedbackDialog>>
>;

interface DialogsController {
  acknowledgementDialog: useDialogType;
  closeWidgetDialog: useDialogType;
  messageFeedbackDialog: MessageFeedbackDialogController;
}

export const Context = createContext({} as DialogsController);

Context.displayName = "DialogsContext";

const DialogsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const acknowledgementDialog = useDialog(AcknowledgementDialog);
  const closeWidgetDialog = useDialog(CloseWidgetDialog);
  const messageFeedbackDialog = useDialog(MessageFeedbackDialog);

  const value = useMemo(
    () => ({
      acknowledgementDialog,
      closeWidgetDialog,
      messageFeedbackDialog,
    }),
    [acknowledgementDialog, closeWidgetDialog, messageFeedbackDialog]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default DialogsProvider;
